import { STEP } from './steps.js'

/**
 * Every word on the order form, in Tagalog, in one file.
 *
 * **One file because the register has to hold.** Copy written screen by screen drifts:
 * one step says *"Ilagay ang numero"* and the next says *"I-type ang cellphone number"*,
 * and a customer who reads slowly notices the seam even when they cannot name it. Kept
 * together, a change of tone is visible at once.
 *
 * The audience decided the language. Machi Express runs in Laguna and nowhere else, and
 * the person this redesign is for has never used a delivery app that was not Messenger.
 * An English label on a Tagalog form is the moment they close the tab.
 *
 * **What stays English, and why.** Order ids (`MCH-`) because they are read back over the
 * phone and typed into Messenger. Nothing else on this screen.
 */

/** The heading of the whole form, above the stepper. */
export const FORM_TITLE = 'Mag-order'

/**
 * One question per screen, phrased as a person would ask it out loud.
 *
 * `title` is the question. `hint` is the one sentence under it, and only where the
 * question cannot carry itself — a hint that repeats the label is noise on a screen whose
 * whole purpose is having nothing extra on it.
 *
 * `short` is what the stepper shows: two or three words, because eight of them sit side
 * by side on a 360px phone.
 */
export const STEP_COPY = {
  [STEP.SERVICE]: {
    short: 'Serbisyo',
    title: 'Ano ang kailangan mo?',
  },
  [STEP.PICKUP]: {
    short: 'Saan kukuha',
    title: 'Saan bibili o kukuha ang rider?',
    hint: 'Hanapin ang tindahan, o gamitin ang lokasyon mo ngayon.',
  },
  [STEP.PICKUP_CONTACT]: {
    short: 'Kontak doon',
    title: 'May makakausap ba doon?',
    hint: 'Kung wala, ang numero mo ang gagamitin ng rider. Pwede mo itong laktawan — pero kung maglalagay ka ng pangalan, kailangan din ang numero.',
  },
  [STEP.ITEMS]: {
    short: 'Listahan',
    title: 'Ano ang kailangan mo?',
    hint: 'Isang linya bawat isa. Maglagay ng bilang sa unahan kung marami.',
  },
  [STEP.BUDGET]: {
    short: 'Budget',
    title: 'Magkano ang tantiya mong gastos?',
    hint: 'Dadagdagan namin ito ng 30% para may sukli kung tumaas ang presyo.',
  },
  [STEP.DROPOFF]: {
    short: 'Saan ihahatid',
    title: 'Saan ihahatid at sino ang tatawagan?',
  },
  [STEP.TIMING]: {
    short: 'Oras',
    title: 'May gusto ka bang oras?',
    hint: 'Kung wala, gagawin ito agad. Pwede mo itong laktawan.',
  },
  [STEP.PAYMENT]: {
    short: 'Bayad',
    title: 'Paano babayaran?',
  },
  [STEP.SUMMARY]: {
    short: 'Buod',
    title: 'Tama ba ang lahat?',
  },
}

/** The buttons at the foot of every screen. */
export const ACTIONS = {
  next: 'Sunod',
  back: 'Bumalik',
  skip: 'Laktawan',
  place: 'Iorder na',
  placing: 'Ipinapadala…',
  edit: 'Palitan',
  retry: 'Subukan ulit',
  waiting: 'Kinukuha ang bayad…',
  confirm: 'Ituloy',
}

export const FIELDS = {
  pickupSource: 'Pangalan ng tindahan o lugar',
  contactName: 'Pangalan',
  contactPhone: 'Cellphone number',
  yourPhone: 'Numerong tatawagan ng rider',
  dropoffName: 'Sino ang tatanggap?',
  dropoffNameHint: 'Kung ikaw rin, hayaan mo itong blangko.',
  items: 'Isulat ang kailangan mo',
  estimatedTotal: 'Tantiyang gastos',
  maxBudget: 'Pinakamataas na gastos',
  maxBudgetHint: 'Pwede mong baguhin ito.',
  pickupTime: 'Oras ng pagkuha',
  deliveryTime: 'Oras ng paghahatid',
  paymentMethod: 'Paano babayaran',
  deliveryFeePaid: 'Kasama na ang delivery fee sa binayad',
  notes: 'May sasabihin ka ba sa rider?',
  notesHint: 'Halimbawa: ipatong sa gate, wag i-doorbell.',
}

/**
 * The two payment answers, each a whole sentence and no second line.
 *
 * They read as *"cash on delivery"* and *"paid online"* before this, which is the
 * industry's vocabulary rather than the customer's — and the pair needed a line of
 * explanation underneath each to mean anything. Two sentences that explain themselves
 * beat two labels that need explaining.
 */
export const PAYMENT_COPY = {
  cash_on_delivery: { label: 'Bayaran pagdating ng rider' },
  online: { label: 'Bayad na, idedeliver na lang' },
}

export const MESSAGES = {
  loadingPartner: 'Sandali lang…',
  partnerClosed: 'Hindi tumatanggap ng order ngayon',
  partnerClosedNote: 'Baka nagpahinga sila, o luma na ang link.',
  browse: 'Tumingin ng iba',
  failed: 'May nangyaring mali',
  tryAgain: 'Pakisubukan ulit.',
  /** Shown once, near the button, because the API gives no field to point at. */
  submitFailed: 'Hindi naipadala ang order.',
  itemsSoFar: 'Ito ang naintindihan namin:',
  noItemsYet: 'Wala pang nakasulat.',
  optional: 'Pwedeng laktawan',

  /**
   * Isinusunod sa `DISPATCH_NOTE.exhausted`, at doon lang.
   *
   * Hindi siya kasama sa mismong note dahil dalawang screen ang bumabasa niyon at iisa
   * lang sa kanila ang may pindutang kanselahin — walang session ang hawak ng nakabukas
   * na link, kaya walang kinakansela doon. Tingnan ang `OrderDetail`.
   */
  canStillCancel: 'Pwede mo pa rin itong kanselahin.',

  /** The summary's fee block. */
  feeLoading: 'Hinahanap ng rider…',
  feeLoadingNote: 'Sandali lang, kinukuha ang eksaktong bayad.',
  feeHeading: 'Delivery fee',
  /** Stated once, quietly. Never a countdown — see useDeliveryQuote. */
  feeMayChange: 'Maaaring magbago ang bayad kung matagalan.',
  feeChanged: 'Nagbago ang bayad',
  feeConfirm: 'Ituloy',
  noRider: 'Walang available na rider ngayon.',
  /**
   * The truth, which is kinder than the instruction it replaced.
   *
   * It read *"Subukan ulit maya-maya"* — leave and come back — while the screen was already
   * re-asking every thirty seconds on its own (`useDeliveryQuote`). So it sent people away
   * from a page that was about to answer them, and it asked them to remember to return.
   */
  noRiderNote: 'Awtomatiko kaming tumitingin kada ilang segundo. Huwag mo lang isara ang order.',
  /** The same check, now rather than at the next tick. */
  noRiderCheck: 'Tingnan kung may rider na',
  noRiderChecking: 'Tinitingnan…',
  feeFailed: 'Hindi makuha ang bayad sa paghahatid.',
  feeRetry: 'Subukan ulit',
}

/**
 * Ano ang aktwal na nangyayari habang `pending` pa ang order.
 *
 * Hindi gumagalaw ang status habang nasa dispatch — walang nangyayari sa panig ng customer
 * hangga't walang rider na tumatanggap — kaya kung wala ito, "Pending" ang nakasulat nang
 * ilang minuto at walang paraan para malaman kung hinahanapan ba sila o walang kumuha.
 *
 * Isang pinagmumulan para sa dalawang screen. May sariling kopya ang `OrderDetail` at ang
 * `OrderSuccess` dati, magkaiba ang laman — tatlong estado sa isa, apat sa isa — at Ingles
 * pareho, sa app na Tagalog ang buong form.
 *
 * **Lahat ng `DISPATCH.STATES` ng server ay narito, kasama ang `scheduled`.** Wala siya sa
 * dalawang lumang kopya. Sa `OrderDetail` ay walang lumalabas na anuman para sa kanya, at
 * sa `OrderSuccess` ay bumabagsak siya sa `unassigned` — *"naghahanap kami ng rider"* —
 * gayong ang buong punto ng estadong iyon ay **sadyang wala pang inaalok kaninuman** hangga't
 * hindi dumarating ang oras (`OrderService.js:908`, `constants.js` DISPATCH.STATES.SCHEDULED).
 */
export const DISPATCH_NOTE = {
  unassigned: 'Naipadala na ang order. Naghahanap kami ng rider.',
  scheduled: 'Nakatakda pa ito. Maghahanap kami ng rider kapag malapit na ang oras.',
  offering: 'May rider na tinatanong ngayon.',
  /**
   * Ang totoo, at ang totoo ay hindi *"nawala ang order mo"*.
   *
   * Naabot na ng bawat rider na pwede ang sarili nilang pagkakataon at walang kumuha. Pero
   * hindi iyon katapusan: **kapag may rider na nag-online, ibinabalik siya ng
   * `sweepExhausted` (`DispatchService.js:612`) sa dispatch nang kusa.** Iyon ang bagay na
   * dapat malaman ng customer, at iyon mismo ang hindi sinasabi ng lumang teksto.
   *
   * Ang lumang teksto ay *"the shop has been asked to sort it out"* — nagpapahiwatig na may
   * hinihintay silang tindahan. Walang hinihintay na tindahan.
   */
  exhausted: 'Walang bakanteng rider sa ngayon. Awtomatiko naming susubukan ulit kapag may rider na nag-online.',
  assigned: 'May rider nang tumanggap. Papunta na siya.',
}

/**
 * @param {string} step
 * @returns {{short: string, title: string, hint?: string}}
 */
export const copyFor = (step) => STEP_COPY[step] ?? { short: '', title: '' }

/** ₱ with thousands separators and two decimals, the way a receipt reads. */
export const peso = (value) => `₱${Number(value ?? 0).toLocaleString('en-PH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`
